import { Icon } from '@assembly-js/design-system';
import React from 'react';
import Stripe from 'stripe';

import PaymentsClient from 'src/clients/PaymentsClient';
import { PlaidIcon } from 'src/components/icons/PlaidIcon';
import { LoadingWrapper } from 'src/components/loading/LoadingWrapper';
import { RouteContext } from 'src/context';
import { useHasACHCapability } from 'src/features/payments/hooks/useHasACHCapability';
import { usePaymentProcessingFee } from 'src/features/payments/hooks/usePaymentProcessingFee';
import { PaymentMethodOption } from 'src/routes/payments/payments-methods/components/PaymentMethodOption';
import { PlaidPaymentMethodOption } from 'src/routes/payments/payments-methods/components/PlaidPaymentMethodOption';
import { PaymentOptions } from 'src/services/api/invoicesApi';

export interface PaymentMethodQueryParams {
  plaidOauthRedirectURL?: string;
  linkToken?: string;
}

export type PaymentMethodType = 'credit_card' | 'plaid' | 'bank';

export interface ChoosePaymentMethodProps {
  onPaymentMethodSelected: (
    paymentMethod: PaymentMethodType,
    createdSource?: Stripe.CustomerSource,
  ) => void;
  isMobileView?: boolean;
  previewMode?: boolean;
  paymentOptions: PaymentOptions;
  amountDue: number;
  currency?: string;
}

const getBankIcon = () => <Icon icon="Bank" className="h-5 w-5" />;
const getBillingIcon = () => <Icon icon="Billing" className="h-5 w-5" />;

export const ChoosePaymentMethod: React.FC<ChoosePaymentMethodProps> = ({
  onPaymentMethodSelected,
  isMobileView = false,
  previewMode = false,
  paymentOptions,
  amountDue,
  currency,
}) => {
  const {
    allowPaymentViaACH,
    allowPaymentViaCC,
    allowPlaidSetup,
    absorbACHFees,
    absorbCreditCardFee,
  } = paymentOptions;
  const { query } = React.useContext(RouteContext);
  const { plaidOauthRedirectURL, linkToken = '' } =
    query as PaymentMethodQueryParams;

  const { shouldBlockBankPayments } = useHasACHCapability();

  const [plaidToken, setPlaidToken] = React.useState(linkToken);
  const [loadingPlaidToken, setLoadingPlaidToken] = React.useState(
    Boolean(allowPlaidSetup && !plaidToken),
  );

  const { creditCardFeeDetails, bankAccountFeeDetails } =
    usePaymentProcessingFee({
      amountDue: amountDue || 0,
      preferences: {
        absorbTransactionFee: [
          paymentOptions.absorbACHFees ? 'bank_account' : '',
          paymentOptions.absorbCreditCardFee ? 'card' : '',
        ].filter(Boolean),
        paymentMethodTypes: [
          paymentOptions.allowPaymentViaACH ? 'bank_account' : '',
          paymentOptions.allowPaymentViaCC ? 'card' : '',
        ].filter(Boolean),
      },
      currency,
    });

  const getPlaidToken = async () => {
    setLoadingPlaidToken(true);
    try {
      const token = await PaymentsClient.getPlaidLinkToken();
      setPlaidToken(token);
    } catch (error) {
      console.error('Error loading plaid token', error);
    }
    setLoadingPlaidToken(false);
  };

  /**
   * On mount generate a new link token if one is not already set from query
   */
  React.useEffect(() => {
    if (allowPlaidSetup && !plaidToken) {
      getPlaidToken();
    }
  }, [plaidToken]);

  return (
    <LoadingWrapper
      isLoading={loadingPlaidToken}
      wrapperProps={{ minHeight: 40 }}
      hideContentWhileLoading
    >
      {allowPaymentViaCC && (
        <PaymentMethodOption
          title="Connect a credit card"
          description="Connect a new credit or debit card."
          feeAmount={!absorbCreditCardFee ? creditCardFeeDetails.feeLabel : ''}
          Icon={getBillingIcon}
          onPaymentMethodOptionSelected={() =>
            onPaymentMethodSelected('credit_card')
          }
          isMobileView={isMobileView}
          previewMode={previewMode}
        />
      )}
      {allowPlaidSetup && (plaidToken || previewMode) && (
        <PlaidPaymentMethodOption
          title="Connect bank using Plaid"
          description="Skip cumbersome verification - Login to your bank account using Plaid"
          feeAmount={!absorbACHFees ? bankAccountFeeDetails.feeLabel : ''}
          Icon={PlaidIcon}
          token={plaidToken}
          receivedRedirectUri={plaidOauthRedirectURL}
          onPaymentMethodOptionSelected={(
            createdSource?: Stripe.CustomerSource,
          ) => onPaymentMethodSelected('plaid', createdSource)}
          previewMode={previewMode}
          isMobileView={isMobileView}
          disabled={shouldBlockBankPayments}
          showACHDisabledTooltip
        />
      )}
      {allowPaymentViaACH && (
        <PaymentMethodOption
          title="Connect bank manually"
          description="Connect bank manually. This method utilizes ACH and microdeposit verification."
          feeAmount={!absorbACHFees ? bankAccountFeeDetails.feeLabel : ''}
          Icon={getBankIcon}
          onPaymentMethodOptionSelected={() => onPaymentMethodSelected('bank')}
          isMobileView={isMobileView}
          previewMode={previewMode}
          disabled={shouldBlockBankPayments}
          showACHDisabledTooltip
        />
      )}
    </LoadingWrapper>
  );
};
